import { supabase } from './SupabaseClient.js';

const USERNAME_PATTERN = /^[a-zA-Z0-9_]{3,20}$/;

function isEmail(value) {
  return value.includes('@');
}

/**
 * Ensures a `profiles` row exists for a signed-in user, creating one from
 * the username stashed in signUp()'s user_metadata if it's missing. Runs
 * after every sign-up/log-in rather than only at sign-up time, because if
 * the Supabase project has "Confirm email" enabled, signUp() returns no
 * session (so the RLS insert would fail) — the profile only gets created
 * once here, on the first login that actually has a session.
 */
async function ensureProfile(user) {
  const { data: existing } = await supabase.from('profiles').select('username').eq('id', user.id).maybeSingle();
  if (existing) return { ok: true, username: existing.username };

  const desiredUsername = user.user_metadata?.username;
  if (!desiredUsername) return { ok: false, error: 'Missing profile info for this account.' };

  const { error } = await supabase.from('profiles').insert({ id: user.id, username: desiredUsername });
  if (error) {
    return { ok: false, error: error.message.includes('duplicate') ? 'That username is already taken.' : error.message };
  }
  return { ok: true, username: desiredUsername };
}

/**
 * Thin wrapper around Supabase Auth. Every method returns
 * { ok, error? , ...} and never throws — account features are additive on
 * top of a fully playable local-guest game, same silent-failure contract
 * as LeaderboardClient, so a misconfigured/unreachable Supabase project
 * degrades to "cloud accounts unavailable" instead of crashing the game.
 */
export const AuthClient = {
  get isConfigured() {
    return supabase !== null;
  },

  /** Resolves to the account username for the current session, or null if signed out/unconfigured. */
  async currentUsername() {
    if (!supabase) return null;
    const { data } = await supabase.auth.getSession();
    if (!data.session) return null;
    const result = await ensureProfile(data.session.user);
    return result.ok ? result.username : null;
  },

  async signUp({ username, email, password }) {
    if (!supabase) return { ok: false, error: 'Cloud accounts are not configured.' };
    const cleanUsername = username.trim();
    if (!USERNAME_PATTERN.test(cleanUsername)) {
      return { ok: false, error: 'Username must be 3-20 characters: letters, numbers, underscore.' };
    }

    const { data, error } = await supabase.auth.signUp({
      email: email.trim(),
      password,
      options: { data: { username: cleanUsername } },
    });
    if (error) return { ok: false, error: error.message };
    if (!data.user) return { ok: false, error: 'Sign up failed.' };

    if (!data.session) {
      // "Confirm email" is enabled on this project — no session yet, so we
      // can't create the profile row (RLS requires auth.uid() = id). It'll
      // be created by ensureProfile() on the first real login instead.
      return { ok: true, confirmed: false, username: cleanUsername };
    }

    const profileResult = await ensureProfile(data.session.user);
    if (!profileResult.ok) return profileResult;
    return { ok: true, confirmed: true, userId: data.session.user.id, username: cleanUsername };
  },

  async logIn({ identifier, password }) {
    if (!supabase) return { ok: false, error: 'Cloud accounts are not configured.' };
    let email = identifier.trim();
    if (!isEmail(email)) {
      const { data: resolvedEmail, error: rpcError } = await supabase.rpc('email_for_username', { lookup_username: email });
      if (rpcError || !resolvedEmail) return { ok: false, error: 'Incorrect username or password.' };
      email = resolvedEmail;
    }

    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { ok: false, error: 'Incorrect username or password.' };

    const profileResult = await ensureProfile(data.session.user);
    if (!profileResult.ok) return profileResult;
    return { ok: true, userId: data.session.user.id, username: profileResult.username };
  },

  async logOut() {
    if (!supabase) return { ok: false };
    await supabase.auth.signOut();
    return { ok: true };
  },
};
